import {Injectable} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot,} from '@angular/router';
import {catchError, map, Observable, of} from 'rxjs';
import {AuthService} from './auth.service';
import {SessionExpiredService} from './session-expired.service';
import {isDefinitiveAuthRefreshFailure} from './auth-session.util';

@Injectable({
    providedIn: 'root',
})
export class AuthGuardService implements CanActivate, CanActivateChild {

    constructor(
        public auth: AuthService,
        public router: Router,
        private sessionExpiredService: SessionExpiredService,
    ) {
    }

    canActivate(
        _route: ActivatedRouteSnapshot,
        _state?: RouterStateSnapshot,
    ): boolean | Observable<boolean> {
        return this.ensureAuthenticated();
    }

    canActivateChild(
        _route: ActivatedRouteSnapshot,
        _state?: RouterStateSnapshot,
    ): boolean | Observable<boolean> {
        // Re-check on every child navigation so soft-expired sessions cannot
        // bypass a parent AuthGuard that already ran once for the layout.
        return this.ensureAuthenticated();
    }

    private ensureAuthenticated(): boolean | Observable<boolean> {
        if (this.auth.isAuthenticated()) {
            this.auth.ensureSessionWatchdog();
            return true;
        }

        if (!this.auth.getRefreshToken()) {
            this.sessionExpiredService.forceExpire('guard-no-refresh');
            return false;
        }

        // Access expired but refresh may still be valid — silent refresh before blocking.
        return this.auth.refreshToken().pipe(
            map(() => {
                this.auth.ensureSessionWatchdog();
                return true;
            }),
            catchError((err) => {
                if (isDefinitiveAuthRefreshFailure(err)) {
                    this.sessionExpiredService.forceExpire('guard-refresh-invalid');
                }
                // Network / 5xx: preserve credentials; deny this navigation (fail closed).
                return of(false);
            }),
        );
    }
}
